import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Container,
  Paper,
  Link,
  IconButton,
  Grid,
  Divider,
  Chip,
  List,
  ListItem,
  Snackbar,
  Alert,
  Stack,
  Tabs,
  Tab,
  CircularProgress
} from '@mui/material';

import InfoIcon from '@mui/icons-material/Info';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import LinkIcon from '@mui/icons-material/Link';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { motion, AnimatePresence } from 'framer-motion';
import InstructionsDialog from './InstructionDialog';
import HottestNews from './HottestNews';
import { useTheme } from '@mui/material/styles';
import { createTheme } from '@mui/material';
import VotingApi from '../apis/voting';
import TurnstileWidget from '../Components/TurnstileWidget';

// Custom theme to match the design
const theme = createTheme({
  // Theme settings remain unchanged
  palette: {
    primary: {
      main: '#4361ee',
    },
    secondary: {
      main: '#e63946',
    },
    success: {
      main: '#2ecc71',
    },
    background: {
      default: 'linear-gradient(to right, #1e2d58, #2e5676)',
      paper: '#ffffff',
    },
    text: {
      primary: '#000000',
      secondary: '#666666',
    },
  },
  typography: {
    h2: {
      fontWeight: 700,
      color: '#ffffff',
      fontSize: '3.5rem',
      textAlign: 'center',
    },
    subtitle1: {
      color: '#ffffff',
      fontSize: '1.2rem',
      textAlign: 'center',
      marginBottom: '2rem',
    },
    h4: {
      fontWeight: 700,
      fontSize: '1.8rem',
      textAlign: 'center',
      marginBottom: '1rem',
    },
    body1: {
      fontSize: '1rem',
      color: '#333333',
    },
    caption: {
      fontSize: '0.9rem',
      color: '#666666',
    },
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
          overflow: 'hidden',
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          textDecoration: 'none',
        },
      },
    },
  },
});

// Animation variants for card transitions
const cardVariants = {
  enter: (direction) => ({
    x: direction > 0 ? 1000 : -1000,
    opacity: 0,
    scale: 0.9,
  }),
  center: {
    x: 0,
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.4,
      type: 'spring',
      stiffness: 120,
      damping: 15
    }
  },
  exit: (direction) => ({
    x: direction < 0 ? 1000 : -1000,
    opacity: 0,
    scale: 0.9,
    transition: {
      duration: 0.3,
      ease: 'easeInOut'
    }
  })
};

// Added loading animation variants
const loadingVariants = {
  animate: {
    scale: [1, 1.2, 1],
    opacity: [0.5, 1, 0.5],
    transition: {
      duration: 1.5,
      repeat: Infinity,
      ease: "easeInOut"
    }
  }
};

const ContentFlaggingInterface = () => {
  const [direction, setDirection] = useState(0);
  const [newsItem, setNewsItem] = useState({});
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(null);
  const [resetVerification, setResetVerification] = useState(0); // New state to track verification resets
  const [verificationRequired, setVerificationRequired] = useState(true); // State to control when verification is needed
  const [keyPointAgreements, setKeyPointAgreements] = useState({});
  const [keyPointDisagreements, setKeyPointDisagreements] = useState({});
  const [keyPointVoted, setKeyPointVoted] = useState({});
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [showInstructions, setShowInstructions] = useState(false);

  // Callback for handling verification completion
  const handleVerify = useCallback((token) => {
    console.log("Verification token received:", token);
    setToken(token);
    setVerificationRequired(false); // Hide verification after successful completion
  }, []);

  // Effect to fetch initial news when verification is completed
  useEffect(() => {
    if (token && tabValue === 0) {
      setLoading(true);
      VotingApi.getNews(newsItem ? newsItem["news_id"] : null, token).then(response => {
        console.log(response.data.data);
        if (tabValue === 0) {
          setNewsItem(response.data.data);
        }
        setLoading(false);
      }).catch(error => {
        setSnackbarMessage("Internal Error, please try again later");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
        setNewsItem(null)
        setLoading(false);
      });
    }
  }, [tabValue, token]);

  // Effect to initialize key point data when news item changes
  useEffect(() => {
    if (newsItem && newsItem.key_points) {
      console.log(newsItem);

      // Initialize agreement and disagreement dictionaries
      const initialAgreements = {};
      const initialDisagreements = {};

      newsItem.key_points.forEach(keyPoint => {
        initialAgreements[keyPoint.index_id] = keyPoint.upvotes;
        initialDisagreements[keyPoint.index_id] = keyPoint.downvotes;
      });

      setKeyPointAgreements(initialAgreements);
      setKeyPointDisagreements(initialDisagreements);

      if (newsItem.marked_points && newsItem.marked_points.length > 0) {
        const voted = {};
        newsItem.marked_points.forEach(markPoint => {
          voted[markPoint] = true;
        });
        setKeyPointVoted(voted);
      } else {
        setKeyPointVoted({});
      }
    }
  }, [newsItem]);

  // Function to handle tab change
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setToken(null);
    if (newValue === 0) setVerificationRequired(true);
    setNewsItem(null);
  };

  // Function to handle voting on key points
  const handleKeyPointVote = (pointIndex, isUpvote) => {
    if (!token) {
      setSnackbarMessage("Please verify that you are human first");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      setVerificationRequired(true);
      return;
    }

    const keyPointId = newsItem.key_points[pointIndex].index_id;

    // Call API to record vote
    VotingApi.vote(newsItem.news_id, keyPointId, isUpvote, token).then(response => {
      console.log(response.data);
      if (isUpvote) {
        setKeyPointAgreements(prev => ({
          ...prev,
          [keyPointId]: (prev[keyPointId] || 0) + 1
        }));
      } else {
        setKeyPointDisagreements(prev => ({
          ...prev,
          [keyPointId]: (prev[keyPointId] || 0) + 1
        }));
      }

    }).catch(error => {
      setSnackbarMessage("Error updating key point votes:");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);

    });

    // Mark this key point as voted
    setKeyPointVoted(prev => ({
      ...prev,
      [keyPointId]: true
    }));

    // Show snackbar notification
    setSnackbarMessage(isUpvote ? "Key point marked as legitimate" : "Key point flagged as misinformation");
    setSnackbarSeverity(isUpvote ? "success" : "warning");
    setSnackbarOpen(true);
  };

  // Function to skip to next article and reset verification
  const moveToNextArticle = () => {
    // Reset verification status
    setToken(null);
    setVerificationRequired(true);
    setResetVerification(prev => prev + 1); // Increment to force reset
    setLoading(true);
    setKeyPointVoted({});
    setKeyPointAgreements({});

  };



  return (
    <Box
      sx={{
        background: 'linear-gradient(to right,rgba(0, 13, 31, 0.8),rgba(2, 49, 33, 0.76))',
        // backgroundImage: 'linear-gradient(to bottom right,rgba(2, 26, 2, 0.98),rgb(10, 41, 2)',
        minHeight: '100vh',
        py: 6,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      <InstructionsDialog showInstructions={showInstructions} setShowInstructions={setShowInstructions} />
      <Container maxWidth="xl">
        <Typography variant="h2" gutterBottom>
          Spot It. Flag It. Help Others.
        </Typography>
        <Stack direction="row" spacing={1} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
          <Typography variant="h6" color='white'>
            Your vote matters — mark misleading content and help build a shared layer of truth across the internet.
            <br />
            <br />
            Click the info button to learn how to use this tool.
          </Typography>
          <IconButton
            onClick={() => setShowInstructions(true)}
            size="large"
            sx={{
              color: 'white',
              '&:hover': {
                bgcolor: 'rgba(255, 255, 255, 0.1)',
              },
              '& .MuiSvgIcon-root': {
                fontSize: '3rem'
              }
            }}
            title="View instructions"
          >
            <InfoIcon sx={{ fontSize: '5rem' }} />
          </IconButton>
        </Stack>

        {/* Tabs for Navigation */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          {/* Conditionally render verification widget */}
          {verificationRequired && (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
              <TurnstileWidget
                onVerify={handleVerify}
                key={`verification-${resetVerification}`} // Key changes to force re-render
              />
            </Box>
          )}

          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            centered
            sx={{
              '& .MuiTab-root': {
                color: 'rgba(255, 255, 255, 0.7)',
                '&.Mui-selected': { color: 'white' }
              },
              '& .MuiTabs-indicator': { backgroundColor: 'white' }
            }}
          >
            <Tab label="Flag Content" />
            <Tab label="Hottest News" />
          </Tabs>
        </Box>

        {/* Content based on selected tab */}
        {tabValue === 0 ? (
          <Grid container spacing={4} justifyContent="center">
            <Grid item xs={12} md={10} lg={9}>
              <Box sx={{ position: 'relative', maxWidth: "100%", mx: 'auto', minHeight: 580 }}>
                {loading ? (
                  // Loading animation
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center',
                      height: '400px',
                      mt: 4
                    }}
                  >
                    <motion.div
                      variants={loadingVariants}
                      animate="animate"
                    >
                      <CircularProgress size={60} thickness={4} color="primary" />
                    </motion.div>
                    <Typography
                      variant="h6"
                      sx={{
                        color: 'white',
                        mt: 3,
                        opacity: 0.9
                      }}
                    >
                      Loading content...
                    </Typography>
                  </Box>
                ) : newsItem && newsItem.title && !verificationRequired ? (
                  <AnimatePresence mode="wait" custom={direction}>
                    <motion.div
                      key={newsItem.id}
                      custom={direction}
                      variants={cardVariants}
                      initial="enter"
                      animate="center"
                      exit="exit"
                    >
                      <Paper elevation={3} sx={{ p: 0, mt: 4, width: '100%', position: 'relative' }}>
                        {/* Skip button in upper right corner */}
                        <IconButton
                          size="medium"
                          onClick={moveToNextArticle}
                          sx={{
                            position: 'absolute',
                            top: 10,
                            right: 10,
                            zIndex: 2,
                            bgcolor: 'rgba(214, 227, 253, 0.76)',
                            '&:hover': { bgcolor: 'rgba(67, 97, 238, 0.1)' }
                          }}
                        >
                          <SkipNextIcon sx={{
                            color: '#000000',
                            width: 35,
                            height: 35,
                          }} />
                        </IconButton>

                        <Box sx={{ p: 3 }}>
                          {/* Article Title and URL */}
                          <Typography variant="h4">{newsItem.title}</Typography>
                          {/* <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
                            <LinkIcon sx={{ mr: 1, fontSize: 16, color: 'primary.main' }} />
                            <Link 
                              href={newsItem.source_link} 
                              target="_blank"
                              rel="noopener noreferrer"
                              color="primary"
                            >
                              {newsItem.source_link}
                            </Link>
                          </Box> */}

                          {/* Article Image */}
                          {/* <Box
                            component="img"
                            src={newsItem.imageUrl}
                            alt="News image"
                            sx={{
                              width: '100%',
                              height: 250,
                              borderRadius: 1,
                              my: 2,
                              objectFit: 'cover',
                            }}
                          /> */}

                          {/* Article Content */}
                          <Typography variant="h5" sx={{ mb: 2 }}>
                            {newsItem.main_idea}
                          </Typography>

                          {/* Source */}
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3 }}>
                            <Typography variant="h6">
                              Source: <Link href={newsItem.source} color="primary">{newsItem.source}</Link>
                            </Typography>
                          </Box>

                          {/* Key Points Section */}
                          <Box sx={{ mt: 4 }}>
                            <Divider sx={{ mb: 2 }}>
                              <Chip label="Key Points" color="primary" />
                            </Divider>
                            {!newsItem || !newsItem.key_points ? <div></div> : <List>
                              {newsItem.key_points.map((keyPoint, idx) => (
                                <React.Fragment key={keyPoint.index_id + newsItem.news_id}>
                                  <ListItem
                                    sx={{
                                      border: '1px solid',
                                      borderColor: keyPointVoted[keyPoint.index_id] ? 'primary.light' : 'grey.300',
                                      borderRadius: 2,
                                      mb: 1,
                                      bgcolor: keyPointVoted[keyPoint.index_id] ? 'rgba(67, 97, 238, 0.05)' : 'transparent',
                                      flexDirection: 'column',
                                      alignItems: 'stretch',
                                      p: 2
                                    }}
                                  >
                                    <Grid container alignItems="center" sx={{ width: '100%' }}>
                                      <Grid item xs={12} sm={8}>
                                        <Typography variant="body1">
                                          {keyPoint.content}
                                        </Typography>
                                      </Grid>
                                      <Grid item xs={12} sm={4} sx={{ display: 'flex', justifyContent: 'flex-end', mt: { xs: 1, sm: 0 } }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                          <IconButton
                                            size="small"
                                            onClick={() => handleKeyPointVote(idx, true)}
                                            color="success"
                                            disabled={keyPointVoted[keyPoint.index_id]}
                                          >
                                            <CheckCircleIcon />
                                          </IconButton>

                                          <IconButton
                                            size="small"
                                            onClick={() => handleKeyPointVote(idx, false)}
                                            color="secondary"
                                            disabled={keyPointVoted[keyPoint.index_id]}
                                          >
                                            <CancelIcon />
                                          </IconButton>
                                        </Box>
                                      </Grid>
                                    </Grid>

                                    {/* Vote Distribution Bar - Only show if there are votes */}
                                    {keyPointVoted[keyPoint.index_id] && (
                                      <Box sx={{ mt: 2, width: '100%' }}>
                                        <Typography variant="body2" sx={{ mb: 0.5, fontSize: '0.8rem', color: 'text.secondary' }}>
                                          Vote Distribution:
                                        </Typography>
                                        <Box sx={{ display: 'flex', width: '100%', height: 12, borderRadius: 6, overflow: 'hidden' }}>
                                          {/* Calculate percentages */}
                                          {(() => {
                                            const agree = keyPointAgreements[keyPoint.index_id] || 0;
                                            const disagree = keyPointDisagreements[keyPoint.index_id] || 0;
                                            const totalVotes = agree + disagree;
                                            const agreePercent = totalVotes > 0 ? (agree / totalVotes) * 100 : 0;
                                            const disagreePercent = totalVotes > 0 ? (disagree / totalVotes) * 100 : 0;

                                            return (
                                              <>
                                                <Box
                                                  sx={{
                                                    bgcolor: 'primary.main',
                                                    width: `${agreePercent}%`,
                                                    display: 'flex',
                                                    justifyContent: 'center',
                                                    alignItems: 'center'
                                                  }}
                                                >
                                                  {agreePercent >= 10 && (
                                                    <Typography variant="caption" sx={{ color: 'white', fontSize: '0.6rem' }}>
                                                      {agree}
                                                    </Typography>
                                                  )}
                                                </Box>
                                                <Box
                                                  sx={{
                                                    bgcolor: 'secondary.main',
                                                    width: `${disagreePercent}%`,
                                                    display: 'flex',
                                                    justifyContent: 'center',
                                                    alignItems: 'center'
                                                  }}
                                                >
                                                  {disagreePercent >= 10 && (
                                                    <Typography variant="caption" sx={{ color: 'white', fontSize: '0.6rem' }}>
                                                      {disagree}
                                                    </Typography>
                                                  )}
                                                </Box>
                                              </>
                                            );
                                          })()}
                                        </Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                                          <Typography variant="caption" sx={{ color: 'primary.main', fontSize: '0.7rem' }}>
                                            Agree: {keyPointAgreements[keyPoint.index_id] || 0}
                                          </Typography>
                                          <Typography variant="caption" sx={{ color: 'secondary.main', fontSize: '0.7rem' }}>
                                            Disagree: {keyPointDisagreements[keyPoint.index_id] || 0}
                                          </Typography>
                                        </Box>
                                      </Box>
                                    )}
                                  </ListItem>
                                </React.Fragment>
                              ))}
                            </List>}
                          </Box>
                        </Box>
                      </Paper>
                    </motion.div>
                  </AnimatePresence>
                ) : verificationRequired && !loading ? (
                  // Display a message when verification is required
                  <Box sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '400px',
                    mt: 4
                  }}>
                    <Typography variant="h6" sx={{ color: 'white' }}>
                      Please complete the verification above to view content.
                    </Typography>
                  </Box>
                ) : (
                  // Fallback for when there's no loading and no data
                  <Box sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '400px',
                    mt: 4
                  }}>
                    <Typography variant="h6" sx={{ color: 'white' }}>
                      No content available. Please try again later.
                    </Typography>
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>
        ) : (
          <HottestNews />
        )}
      </Container>

      {/* Feedback Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={2000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          elevation={6}
          variant="filled"
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ContentFlaggingInterface;